import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { 
  Box, 
  Button, 
  FormControl, 
  FormLabel, 
  Input, 
  VStack, 
  Heading, 
  Text, 
  useToast,
  InputGroup,
  InputRightElement,
  IconButton,
  Flex,
  FormErrorMessage,
  Progress,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { CheckIcon, CloseIcon } from '@chakra-ui/icons';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm_password: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  // See LoginPage — same reviewer-invite handoff. The invite token rides
  // along through registration so the new account lands directly on the
  // accept handshake instead of the onboarding flow.
  const registerParams = new URLSearchParams(location.search);
  const inviteToken = registerParams.get('invite');
  const eventInviteToken = registerParams.get('eventInvite');

  useEffect(() => {
    if (!formData.password) {
      setPasswordStrength(0);
      return;
    }
    
    let strength = 0;
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(formData.password);
    
    if (formData.password.length >= 8) strength += 20;
    if (/[A-Z]/.test(formData.password)) strength += 20;
    if (/[a-z]/.test(formData.password)) strength += 20;
    if (/[0-9]/.test(formData.password)) strength += 20;
    if (hasSpecialChar) strength += 20;
    
    setPasswordStrength(strength);
  }, [formData.password]);
  
  const getStrengthColor = () => {
    if (passwordStrength < 20) return "red.500";
    if (passwordStrength < 40) return "orange.500";
    if (passwordStrength < 80) return "yellow.500";
    return "green.500";
  };
  
  const getStrengthLabel = () => {
    if (passwordStrength < 20) return "Weak";
    if (passwordStrength < 40) return "Fair";
    if (passwordStrength < 100) return "Good";
    return "Strong";
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else {
      if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters long';
      } else if (!/[A-Z]/.test(formData.password)) {
        newErrors.password = 'Password must contain at least one uppercase letter';
      } else if (!/[a-z]/.test(formData.password)) {
        newErrors.password = 'Password must contain at least one lowercase letter';
      } else if (!/[0-9]/.test(formData.password)) {
        newErrors.password = 'Password must contain at least one number';
      }
    }
    
    if (!formData.confirm_password) {
      newErrors.confirm_password = 'Please confirm your password';
    } else if (formData.password !== formData.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
    
    if (errors[id]) {
      setErrors(prev => ({
        ...prev,
        [id]: undefined
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      const result = await register(formData);
      
      if (result.success) {
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', {
            'send_to': 'AW-17807965020/6vJcCLvf_NEbENz2v6tC',
            'value': 1.0,
            'currency': 'USD',
            'transaction_id': result.user?.id || Date.now().toString()
          });
        }

        toast({
          title: 'Registration successful!',
          description: 'Welcome to GillyReach!',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        if (eventInviteToken) {
          navigate(`/events/accept/${encodeURIComponent(eventInviteToken)}`);
        } else if (inviteToken) {
          navigate(`/reviews/accept/${encodeURIComponent(inviteToken)}`);
        } else {
          sessionStorage.setItem('show_seller_profile_setup', 'true');
          navigate('/');
        }
      } else {
        toast({
          title: 'Registration failed',
          description: result.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'An error occurred',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const inputProps = {
    bg: "#171717",
    borderColor: "rgba(255,255,255,0.12)",
    color: "#fafafa",
    fontSize: "sm",
    _placeholder: { color: 'rgba(255,255,255,0.25)' },
    _hover: { borderColor: 'rgba(255,255,255,0.2)' },
    _focus: { borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' },
  };

  return (
    <Flex align="center" justify="center" minH="100vh" py={10} bg="#0a0a0a">
      <Box 
        maxW="md" 
        w="100%" 
        p={10}
        borderRadius="12px"
        border="1px solid rgba(255,255,255,0.10)"
        bg="#0f0f0f"
      >
        <VStack spacing={8}>
          <Box mb={2}>
            <img 
              src="/gillyreach-favicon.png" 
              alt="GillyReach" 
              style={{ height: '52px', width: 'auto', margin: '0 auto' }}
            />
          </Box>
          <Heading
            size="lg"
            color="#fafafa"
            fontWeight="700"
            letterSpacing="-0.02em"
          >
            Create account
          </Heading>
          
          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <VStack spacing={5} align="stretch">
              <FormControl id="first_name" isRequired isInvalid={errors.first_name}>
                <FormLabel fontSize="sm" color="#a3a3a3" mb={1.5}>First Name</FormLabel>
                <Input 
                  type="text" 
                  value={formData.first_name} 
                  onChange={handleChange} 
                  placeholder="Your first name"
                  {...inputProps}
                />
                <FormErrorMessage fontSize="xs">{errors.first_name}</FormErrorMessage>
              </FormControl>
              
              <FormControl id="last_name" isRequired isInvalid={errors.last_name}>
                <FormLabel fontSize="sm" color="#a3a3a3" mb={1.5}>Last Name</FormLabel>
                <Input 
                  type="text" 
                  value={formData.last_name} 
                  onChange={handleChange} 
                  placeholder="Your last name"
                  {...inputProps}
                />
                <FormErrorMessage fontSize="xs">{errors.last_name}</FormErrorMessage>
              </FormControl>
              
              <FormControl id="email" isRequired isInvalid={errors.email}>
                <FormLabel fontSize="sm" color="#a3a3a3" mb={1.5}>Work Email</FormLabel>
                <Input 
                  type="email" 
                  value={formData.email} 
                  onChange={handleChange}
                  placeholder="your@work-email.com"
                  {...inputProps}
                />
                <FormErrorMessage fontSize="xs">{errors.email}</FormErrorMessage>
              </FormControl>
              
              <FormControl id="password" isRequired isInvalid={errors.password}>
                <FormLabel fontSize="sm" color="#a3a3a3" mb={1.5}>Password</FormLabel>
                <InputGroup>
                  <Input 
                    type={showPassword ? 'text' : 'password'} 
                    value={formData.password} 
                    onChange={handleChange}
                    placeholder="Choose a password"
                    {...inputProps}
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      icon={showPassword ? <FiEyeOff /> : <FiEye />}
                      size="sm"
                      variant="ghost"
                      color="#a3a3a3"
                      onClick={() => setShowPassword(!showPassword)}
                      _hover={{ color: '#fafafa', bg: 'transparent' }}
                      _focus={{ boxShadow: 'none' }}
                    />
                  </InputRightElement>
                </InputGroup>
                {formData.password && (
                  <Box mt={2.5}>
                    <Flex align="center" justify="space-between" mb={1.5}>
                      <Text fontSize="xs" color={getStrengthColor()}>
                        {getStrengthLabel()}
                      </Text>
                      <Text fontSize="xs" color="rgba(255,255,255,0.3)">
                        {passwordStrength}%
                      </Text>
                    </Flex>
                    <Progress
                      value={passwordStrength}
                      colorScheme={getStrengthColor().split('.')[0]}
                      size="xs"
                      borderRadius="full"
                      bg="rgba(255,255,255,0.08)"
                    />
                    
                    <Box mt={3}>
                      <Text fontSize="xs" color="rgba(255,255,255,0.3)" mb={1.5}>Password must have:</Text>
                      <List spacing={1}>
                        {[
                          { check: formData.password.length >= 8, label: 'At least 8 characters' },
                          { check: /[A-Z]/.test(formData.password), label: 'At least one uppercase letter' },
                          { check: /[a-z]/.test(formData.password), label: 'At least one lowercase letter' },
                          { check: /[0-9]/.test(formData.password), label: 'At least one number' },
                        ].map(({ check, label }) => (
                          <ListItem key={label} display="flex" alignItems="center">
                            <ListIcon
                              as={check ? CheckIcon : CloseIcon}
                              color={check ? "#4ade80" : "rgba(255,255,255,0.25)"}
                              boxSize={2.5}
                            />
                            <Text fontSize="xs" color={check ? "#4ade80" : "rgba(255,255,255,0.4)"}>
                              {label}
                            </Text>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </Box>
                )}
                <FormErrorMessage fontSize="xs">{errors.password}</FormErrorMessage>
              </FormControl>
              
              <FormControl id="confirm_password" isRequired isInvalid={errors.confirm_password}>
                <FormLabel fontSize="sm" color="#a3a3a3" mb={1.5}>Confirm Password</FormLabel>
                <InputGroup>
                  <Input 
                    type={showConfirmPassword ? 'text' : 'password'} 
                    value={formData.confirm_password} 
                    onChange={handleChange}
                    placeholder="Confirm your password"
                    {...inputProps}
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      icon={showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                      size="sm"
                      variant="ghost"
                      color="#a3a3a3"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      _hover={{ color: '#fafafa', bg: 'transparent' }}
                      _focus={{ boxShadow: 'none' }}
                    />
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage fontSize="xs">{errors.confirm_password}</FormErrorMessage>
              </FormControl>
              
              <Button 
                type="submit"
                bg="#fafafa"
                color="#0a0a0a"
                borderRadius="full"
                fontWeight="600"
                fontSize="sm"
                h="40px"
                width="full" 
                mt={2}
                isLoading={isLoading}
                loadingText="Creating account..."
                _hover={{ opacity: 0.85 }}
              >
                Create account
              </Button>
            </VStack>
          </form>
          
          <Text fontSize="sm" color="#a3a3a3">
            Already have an account?{' '}
            <Link
              to={eventInviteToken ? `/login?eventInvite=${encodeURIComponent(eventInviteToken)}` : inviteToken ? `/login?invite=${encodeURIComponent(inviteToken)}` : '/login'}
              style={{ color: 'rgba(255,255,255,0.65)', textDecoration: 'underline' }}
            >
              Sign in
            </Link>
          </Text>
        </VStack>
      </Box>
    </Flex>
  );
};

export default RegisterPage;
